import * as PIXI from 'pixi.js';
import { game } from '../../Game';
import { gameRandom } from '../../utils/GameRandom';
import { CUT_MARGIN, outlineOf, outlinePoint } from './laserTracking';
import { EDGE_STAINS, stainEdge } from './laserGore';

const { getPointer, NULL } = Box2D;

const MOVED_JOINTS = {
	head: ['eye_left_joint'],
	body: ['shoulder_right_joint'],
	belly: ['thigh_right_joint'],
};
const LEAVING_LIMBS = {
	body: ['shoulder_right', 'arm_right', 'hand_right'],
	belly: ['thigh_right', 'leg_right', 'feet_right'],
};
const CONNECTIONS = [['head_joint', 'head', 'body'], ['belly_joint', 'body', 'belly']];
const ALIGN_DISTANCE = 0.3;
const ALIGN_ANGLE = 0.35;
const HUMANOID_KICK = 2.5;
const HUMANOID_SPIN = 1.2;
const SLIDE_SPEED = 3;
const SLIDE_SPIN = 3;

// Organs showing in an adult's cut face, laid out in the flesh art's box
// [x, y, width, height]: texture, centre x, centre y and width as fractions of
// the box, and a turn.
const ORGANS = {
	body: {box: [41, 6, 38, 103], parts: [
		['Gore_LungRight0000', 0.5, 0.33, 0.72, 0],
		['Gore_Stomach0000', 0.52, 0.66, 0.78, 0],
		['Gore_Liver0000', 0.45, 0.8, 0.7, 0],
	]},
	belly: {box: [24, 4, 35, 40], parts: [
		['Gore_Intestine0000', 0.5, 0.42, 0.78, Math.PI / 2],
		['Gore_Intestine0000', 0.45, 0.6, 0.82, -Math.PI / 2],
		['Gore_Intestine0000', 0.55, 0.77, 0.66, Math.PI / 2],
	]},
};

const vec = new Box2D.b2Vec2(0, 0);

const copyShape = (fixture, place) => {
	const shape = fixture.GetShape();
	if (shape.GetType() === Box2D.b2Shape.e_circle) {
		const source = Box2D.castObject(shape, Box2D.b2CircleShape);
		const circle = new Box2D.b2CircleShape();
		circle.set_m_radius(source.get_m_radius());
		const center = source.get_m_p();
		const point = place(center.get_x(), center.get_y());
		vec.Set(point.x, point.y);
		circle.set_m_p(vec);
		return circle;
	}
	if (shape.GetType() === Box2D.b2Shape.e_polygon) {
		const source = Box2D.castObject(shape, Box2D.b2PolygonShape);
		const vertices = [];
		for (let i = 0; i < source.get_m_count(); i++) {
			const vertex = source.get_m_vertices(i);
			vertices.push(place(vertex.get_x(), vertex.get_y()));
		}
		const polygon = new Box2D.b2PolygonShape();
		const [pointer, destroyVertices] = Box2D.pointsToVec2Array(vertices);
		polygon.Set(pointer, vertices.length);
		destroyVertices();
		return polygon;
	}
	return null;
};

const samePlace = (x, y) => ({x, y});

// Flipping a prefab mirrors the live fixtures without touching the level data,
// so the copy takes its fixtures from the body itself.
const copyFixtures = (source, target, place) => {
	const old = [];
	for (let fixture = target.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) old.push(fixture);
	old.forEach(fixture => target.DestroyFixture(fixture));
	for (let fixture = source.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) {
		const shape = copyShape(fixture, place);
		if (!shape) continue;
		const fixtureDef = new Box2D.b2FixtureDef();
		fixtureDef.set_shape(shape);
		fixtureDef.set_density(fixture.GetDensity());
		fixtureDef.set_friction(fixture.GetFriction());
		fixtureDef.set_restitution(fixture.GetRestitution());
		fixtureDef.set_isSensor(fixture.IsSensor());
		const filter = fixtureDef.get_filter();
		const original = fixture.GetFilterData();
		filter.set_categoryBits(original.get_categoryBits());
		filter.set_maskBits(original.get_maskBits());
		filter.set_groupIndex(original.get_groupIndex());
		target.CreateFixture(fixtureDef);
		Box2D.destroy(fixtureDef);
		Box2D.destroy(shape);
	}
	target.ResetMassData();
};

export const setGroupIndex = (body, group) => {
	for (let fixture = body.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) {
		const filter = fixture.GetFilterData();
		filter.set_groupIndex(group);
		fixture.SetFilterData(filter);
	}
};

export const cloneHalf = (back, keepPrefab, place = samePlace) => {
	const data = Object.assign(new game.editor.bodyObject(), JSON.parse(JSON.stringify(back.mySprite.data)));
	data.refName = '';
	if (!keepPrefab) {
		data.groups = '';
		data.prefabInstanceName = '';
		delete data.subPrefabInstanceName;
	}
	const position = back.GetPosition();
	data.x = position.x;
	data.y = position.y;
	data.rotation = back.GetAngle();
	data.fixed = false;
	const front = game.editor.buildBodyFromObj(data);
	copyFixtures(back, front, place);
	front.mySprite.scale.x = back.mySprite.scale.x;
	front.SetAwake(true);
	return front;
};

const setVelocity = (body, source, extraX, extraY, extraSpin) => {
	const velocity = source.GetLinearVelocity();
	vec.Set(velocity.x + extraX, velocity.y + extraY);
	body.SetLinearVelocity(vec);
	body.SetAngularVelocity(source.GetAngularVelocity() + extraSpin);
};

// An empty texture container placed on its body the same way as the skin it replaces.
export const frameLike = skin => {
	const frame = new PIXI.Container();
	frame.pivot.copyFrom(skin.pivot);
	frame.scale.copyFrom(skin.scale);
	frame.alpha = skin.alpha;
	frame.data = Object.assign(new game.editor.textureObject(), JSON.parse(JSON.stringify(skin.data)));
	frame.data.refName = '';
	frame.data.isCarvable = false;
	return frame;
};

export const attachFrame = (body, frame, skin) => {
	const data = skin.data;
	game.editor.setTextureToBody(body, frame, data.texturePositionOffsetLength, data.texturePositionOffsetAngle, data.textureAngleOffset);
	game.editor.updateBodyPosition(body);
};

// The front half keeps the original skin container (with its wounds); the back
// half gets a new container in the same slot, just below it.
export const swapHalfTextures = (back, front, cutTexture) => {
	const skin = back.myTexture;
	const meat = frameLike(skin);
	const sprite = new PIXI.Sprite(cutTexture);
	if (skin.myFlesh && cutTexture === skin.myFlesh.texture) sprite.tint = skin.myFlesh.tint;
	meat.addChild(sprite);
	meat.originalSprite = sprite;
	meat.myFlesh = sprite;
	const textures = game.editor.textures;
	textures.addChildAt(meat, textures.getChildIndex(skin));
	attachFrame(back, meat, skin);
	attachFrame(front, skin, skin);
};

// Wounds already on the skin fall away with it. The back half keeps its decal
// cache so the editor never builds a new one for it, but without an entry it
// takes no further decals.
const transferDecals = (back, front) => {
	if (!back.myRTCache) return;
	shareDecals(back, front);
	back.myDecalEntry = null;
};

export const shareDecals = (back, front) => {
	front.myRTCache = back.myRTCache;
	front.myDecalEntry = back.myDecalEntry;
	front.decalLookupKey = back.decalLookupKey;
};

export const copyRevoluteJoint = (joint, bodyA, bodyB) => {
	if (joint.GetType() !== Box2D.e_revoluteJoint) return null;
	const revolute = Box2D.castObject(joint, Box2D.b2RevoluteJoint);
	const jointDef = new Box2D.b2RevoluteJointDef();
	jointDef.set_bodyA(bodyA);
	jointDef.set_bodyB(bodyB);
	jointDef.set_localAnchorA(revolute.GetLocalAnchorA());
	jointDef.set_localAnchorB(revolute.GetLocalAnchorB());
	jointDef.set_referenceAngle(revolute.GetReferenceAngle());
	jointDef.set_enableLimit(revolute.IsLimitEnabled());
	jointDef.set_lowerAngle(revolute.GetLowerLimit());
	jointDef.set_upperAngle(revolute.GetUpperLimit());
	jointDef.set_enableMotor(revolute.IsMotorEnabled());
	jointDef.set_motorSpeed(revolute.GetMotorSpeed());
	jointDef.set_maxMotorTorque(revolute.GetMaxMotorTorque());
	jointDef.set_collideConnected(revolute.GetCollideConnected());
	const copy = game.editor.CreateJoint(jointDef);
	Box2D.destroy(jointDef);
	return copy;
};

const rehomeJoint = (lookup, name, from, to) => {
	const joint = lookup[name];
	if (!joint || joint.destroyed) return;
	const bodyA = joint.GetBodyA() === from ? to : joint.GetBodyA();
	const bodyB = joint.GetBodyB() === from ? to : joint.GetBodyB();
	if (bodyA !== to && bodyB !== to) return;
	const copy = copyRevoluteJoint(joint, bodyA, bodyB);
	if (!copy) return;
	game.editor.deleteObjects([joint]);
	lookup[name] = copy;
};

const aligned = (front, back) => {
	if (!front || front.destroyed || !back || back.destroyed) return false;
	const a = front.GetPosition();
	const b = back.GetPosition();
	const dx = a.x - b.x;
	const dy = a.y - b.y;
	return dx * dx + dy * dy < ALIGN_DISTANCE * ALIGN_DISTANCE && Math.abs(front.GetAngle() - back.GetAngle()) < ALIGN_ANGLE;
};

// Front limbs let go of handlebars, pedals and grabs, so the front half can fall away.
const releaseLimb = (humanoid, limb) => {
	const own = humanoid.lookupObject._bodies;
	const external = [];
	for (let edge = limb.GetJointList(); getPointer(edge) !== getPointer(NULL); edge = edge.get_next()) {
		const joint = game.editor.CastJoint(edge.joint);
		const other = joint.GetBodyA() === limb ? joint.GetBodyB() : joint.GetBodyA();
		if (!own.includes(other)) external.push(joint);
	}
	external.forEach(joint => {
		if (!joint.destroyed) game.editor.deleteObjects([joint]);
	});
	delete limb.grabJoints;
};

export const makeFrontHalf = (humanoid, back, part) => {
	const front = cloneHalf(back, true);
	front.isFlesh = back.isFlesh;
	front.isHumanoid = true;
	front.bloodSkin = back.bloodSkin;
	front.noDamage = true;
	front.depthCut = 'front';
	const fronts = humanoid.depthCutFronts || (humanoid.depthCutFronts = {});
	fronts[part] = front;
	return front;
};

// Moves the part's front joints over and joins it to neighbouring front halves
// that were cut together with it.
export const releaseFrontHalf = (humanoid, back, front, part) => {
	const lookup = humanoid.lookupObject;
	const fronts = humanoid.depthCutFronts;
	MOVED_JOINTS[part].forEach(name => rehomeJoint(lookup, name, back, front));
	CONNECTIONS.forEach(([name, partA, partB]) => {
		if (partA !== part && partB !== part) return;
		const other = partA === part ? partB : partA;
		const joint = lookup[name];
		if (!joint || joint.destroyed || !aligned(fronts[other], lookup[other])) return;
		const swap = body => body === lookup[partA] ? fronts[partA] : body === lookup[partB] ? fronts[partB] : null;
		const bodyA = swap(joint.GetBodyA());
		const bodyB = swap(joint.GetBodyB());
		if (bodyA && bodyB) copyRevoluteJoint(joint, bodyA, bodyB);
	});
};

export const slideOff = (front, back) => {
	const angle = gameRandom() * Math.PI * 2;
	setVelocity(front, back, Math.cos(angle) * SLIDE_SPEED, Math.sin(angle) * SLIDE_SPEED, (gameRandom() * 2 - 1) * SLIDE_SPIN);
};

const addOrgans = (frame, part) => {
	const {box: [x, y, width, height], parts} = ORGANS[part];
	for (const [name, cx, cy, size, turn] of parts) {
		const organ = new PIXI.Sprite(PIXI.Texture.from(name));
		organ.anchor.set(0.5);
		organ.position.set(x + cx * width, y + cy * height);
		organ.rotation = turn;
		organ.scale.set(size * width / (turn ? organ.texture.height : organ.texture.width));
		frame.addChild(organ);
	}
};

export const splitTorsoPart = (humanoid, back, part) => {
	const lookup = humanoid.lookupObject;
	const front = makeFrontHalf(humanoid, back, part);
	const skin = back.myTexture;
	swapHalfTextures(back, front, skin.myFlesh ? skin.myFlesh.texture : skin.originalSprite.texture);
	if (!humanoid.noGoreParticles && skin.data.textureName.startsWith('Normal_')) addOrgans(back.myTexture, part);
	transferDecals(back, front);
	// Stains just inside the edge of the skin half, where the two halves came apart.
	const outline = outlineOf(back.depthCutState.shapes);
	const inset = CUT_MARGIN.humanoid * 1.5;
	const position = front.GetPosition();
	const c = Math.cos(front.GetAngle());
	const s = Math.sin(front.GetAngle());
	const stains = [];
	for (let i = 0; i < EDGE_STAINS; i++) {
		const point = outlinePoint(outline, (i + Math.random()) / EDGE_STAINS);
		const x = point.x - point.nx * inset;
		const y = point.y - point.ny * inset;
		stains.push({x: position.x + c * x - s * y, y: position.y + s * x + c * y});
	}
	stainEdge(front, stains);
	LEAVING_LIMBS[part].forEach(name => {
		const limb = lookup[name];
		if (!limb || limb.destroyed) return;
		limb.snapped = true;
		releaseLimb(humanoid, limb);
	});
	releaseFrontHalf(humanoid, back, front, part);
	const facing = humanoid.flipped ? -1 : 1;
	const angle = back.GetAngle();
	setVelocity(front, back, Math.cos(angle) * HUMANOID_KICK * facing, Math.sin(angle) * HUMANOID_KICK * facing, HUMANOID_SPIN * facing);
};
